import { useEffect, useState } from "react";
import { Loader2, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const REASON = { no_page: "No page", low_clicks: "Rarely clicked", low_rank: "Below page 1" };
const SOURCE = { google: "Google", site: "On-site", both: "Google + On-site" };
const slugId = (q) => q.replace(/[^a-z0-9]+/g, "-").slice(0, 40);

function Plan({ s, id }) {
  return (
    <div className="mt-3 bg-emerald-50/50 border border-emerald-100 rounded-md p-3 text-sm" data-testid={`gap-plan-${id}`}>
      <div className="font-semibold text-slate-900" data-testid={`gap-plan-title-${id}`}>{s.title}</div>
      <div className="text-xs font-mono text-emerald-700">{s.slug}</div>
      <p className="text-xs text-slate-600 mt-1">{s.meta_description}</p>
      <ol className="list-decimal pl-5 mt-2 text-slate-700 space-y-0.5">{s.outline.map((o, i) => <li key={i}>{o}</li>)}</ol>
    </div>
  );
}

function GapRow({ r }) {
  const id = slugId(r.query);
  const [s, setS] = useState(r.suggestion);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const gen = () => {
    setBusy(true);
    api.post("/admin/content-gaps/suggest", { query: r.query }).then((x) => { setS(x.data); setOpen(true); })
      .catch(() => toast.error("Couldn't write a suggestion. Please try again.")).finally(() => setBusy(false));
  };
  return (
    <div className="px-5 py-3" data-testid={`gap-row-${id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <div className="flex-1 min-w-[180px] font-semibold text-slate-900">{r.query}</div>
        <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border border-slate-200 text-slate-500">{SOURCE[r.source]}</span>
        {r.reasons.map((x) => <span key={x} className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">{REASON[x]}</span>)}
        <span className="text-xs text-slate-500 w-full sm:w-56 sm:text-right" data-testid={`gap-stats-${id}`}>
          {r.source !== "site" && `${r.impressions} impr · ${(r.ctr * 100).toFixed(1)}% CTR · pos ${r.position.toFixed(1)}`}
          {r.source === "both" && " · "}
          {r.site_searches > 0 && `${r.site_searches} on-site search${r.site_searches === 1 ? "" : "es"}`}
        </span>
        {s ? <Button size="sm" variant="ghost" onClick={() => setOpen(!open)} data-testid={`gap-toggle-${id}`}>{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}Page plan</Button>
          : <Button size="sm" variant="outline" disabled={busy} onClick={gen} data-testid={`gap-suggest-${id}`}>{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}Suggest page</Button>}
      </div>
      {s && open && <Plan s={s} id={id} />}
    </div>
  );
}

export function ContentGaps() {
  const [days, setDays] = useState(28);
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get("/admin/content-gaps", { params: { days } }).then((r) => setD(r.data)).catch(() => setD({ rows: [], google_connected: false, google_error: "Couldn't load content gaps" }));
  }, [days]);
  return (
    <div className="bg-white border border-slate-200 rounded-lg mt-4" data-testid="content-gaps">
      <div className="p-5 flex flex-wrap items-center gap-3 border-b border-slate-100">
        <div>
          <div className="font-semibold text-slate-900">Content gaps — searches with no good page</div>
          <div className="text-xs text-slate-500">Searches people make that GrantVerify doesn't answer well yet. Write these pages next.</div>
        </div>
        <div className="ml-auto flex gap-1">{[7, 28, 90].map((n) => <button key={n} onClick={() => setDays(n)} data-testid={`gaps-days-${n}`} className={`px-3 py-1 rounded-full text-xs border ${days === n ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600"}`}>{n}d</button>)}</div>
      </div>
      {!d ? <Loader2 className="h-5 w-5 m-5 animate-spin text-emerald-600" /> : (
        <>
          {!d.google_connected && <p className="px-5 py-2 text-xs text-slate-500 bg-slate-50 border-b border-slate-100" data-testid="gaps-site-only">Showing on-site searches only. Google searches will appear once Search Console is connected.</p>}
          {d.google_error && <p className="px-5 py-2 text-xs text-red-600 border-b border-slate-100" data-testid="gaps-google-error">{d.google_error}</p>}
          <div className="divide-y divide-slate-100 max-h-[28rem] overflow-y-auto">
            {d.rows.length === 0 ? <p className="p-5 text-sm text-slate-500" data-testid="gaps-empty">No content gaps found for this period.</p> : d.rows.map((r) => <GapRow key={r.query} r={r} />)}
          </div>
        </>
      )}
    </div>
  );
}
